import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import Toggle from '../ui/Toggle';
import { useConfirm } from '../ui/ConfirmDialog';
import {
  BuildingIcon,
  CopyIcon,
  CheckIcon,
  PlusIcon,
} from '../icons';
import { PhoneNumberTable } from './PhoneNumberTable';
import type { ConnectedNumber } from '../../lib/whatsappApi';

export interface WabaAccountCardProps {
  businessAccountId: string;
  numbers: ConnectedNumber[];
  defaultId: string | null;
  manageId: string | null;
  busyId: string | null;
  onManage: (id: string) => void;
  onRefreshHealth: (id: string) => Promise<void>;
  onMakeDefault: (id: string) => Promise<void>;
  onTogglePause: (id: string, currentlyPaused: boolean) => Promise<void>;
  onRemove: (number: ConnectedNumber) => Promise<void>;
  onAddPhoneNumber: (wabaId: string) => void;
}

export function WabaAccountCard({
  businessAccountId,
  numbers,
  defaultId,
  manageId,
  busyId,
  onManage,
  onRefreshHealth,
  onMakeDefault,
  onTogglePause,
  onRemove,
  onAddPhoneNumber,
}: WabaAccountCardProps) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [copied, setCopied] = useState(false);
  const [wabaBusy, setWabaBusy] = useState(false);

  function copyWabaId() {
    void navigator.clipboard?.writeText(businessAccountId);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  // A WABA is considered paused if all its numbers are paused
  const allPaused = numbers.length > 0 && numbers.every((n) => n.isPaused);
  const anyPaused = numbers.some((n) => n.isPaused);

  // WABA level display name derived from first number label or verifiedName
  const wabaName = numbers.find((n) => n.health?.verifiedName)?.health?.verifiedName ||
    numbers.find((n) => n.label)?.label ||
    `WABA ${businessAccountId.slice(-6)}`;

  async function handleWabaToggle(nextOn: boolean) {
    // nextOn = true means enable (unpause), nextOn = false means disable (pause)
    const nextPaused = !nextOn;
    const confirmMsg = nextPaused
      ? t('connect.waba.pauseConfirm')
      : t('connect.waba.resumeConfirm');

    if (!(await confirm({ title: t('common.areYouSure'), body: confirmMsg, confirmLabel: t('common.confirm'), danger: nextPaused }))) return;

    setWabaBusy(true);
    try {
      // Toggle each number in this WABA
      for (const n of numbers) {
        if (Boolean(n.isPaused) !== nextPaused) {
          await onTogglePause(n.id, Boolean(n.isPaused));
        }
      }
    } finally {
      setWabaBusy(false);
    }
  }

  return (
    <div className="rounded-card border border-line bg-white shadow-card overflow-hidden dark:border-slate-800 dark:bg-slate-900">
      {/* WABA Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface/30 p-4 sm:px-5 dark:border-slate-800 dark:bg-slate-800/30">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint">
            <BuildingIcon size={20} />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-extrabold text-ink dark:text-slate-100">
                {wabaName}
              </h2>
              {allPaused ? (
                <Badge variant="warning" size="sm" dot>
                  {t('connect.waba.pausedBadge')}
                </Badge>
              ) : anyPaused ? (
                <Badge variant="warning" size="sm" dot>
                  {t('connect.status.partiallyPaused')}
                </Badge>
              ) : (
                <Badge variant="success" size="sm" dot>
                  {t('connect.status.active')}
                </Badge>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-3 dark:text-slate-400">
              <button
                type="button"
                onClick={copyWabaId}
                className="group inline-flex items-center gap-1 font-mono hover:text-brand"
                title={businessAccountId}
              >
                <span>{t('connect.waba.id')}: {businessAccountId}</span>
                {copied ? (
                  <CheckIcon size={12} className="text-success" />
                ) : (
                  <CopyIcon size={12} className="opacity-60 group-hover:opacity-100" />
                )}
              </button>
              <span>•</span>
              <span>{numbers.length} {t('connect.stats.phoneNumbers').toLowerCase()}</span>
            </div>
          </div>
        </div>

        {/* WABA Actions */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ink-2 dark:text-slate-300">
              {allPaused ? t('connect.waba.enable') : t('connect.waba.disable')}
            </span>
            <Toggle
              on={!allPaused}
              onChange={(nextOn) => {
                if (!wabaBusy) void handleWabaToggle(nextOn);
              }}
              label={t('connect.waba.enable')}
            />
          </div>

          <Button
            size="sm"
            variant="secondary"
            disabled={wabaBusy}
            onClick={() => onAddPhoneNumber(businessAccountId)}
            className="text-xs"
          >
            <PlusIcon size={13} className="me-1" />
            {t('connect.waba.addNumber')}
          </Button>
        </div>
      </div>

      {/* Phone Numbers Table / List */}
      <div className="p-4 sm:p-5">
        <PhoneNumberTable
          numbers={numbers}
          defaultId={defaultId}
          manageId={manageId}
          busyId={busyId}
          onManage={onManage}
          onRefreshHealth={onRefreshHealth}
          onMakeDefault={onMakeDefault}
          onTogglePause={onTogglePause}
          onRemove={onRemove}
        />
      </div>
    </div>
  );
}

export default WabaAccountCard;
